/**
 * Project edit modal — Loader tab (iter 29).
 *
 * Replaces the portal's old admin Settings → Operations tab. Each
 * project owns its loader schedule + history. When the cron fires
 * the builder's background scheduler calls
 * ``reload_project_data_source`` (the same path that the existing
 * "Reload" button in the Data tab uses), so the data refresh path
 * is shared — only the trigger changes.
 *
 * The component is self-contained: it owns the schedule edit form,
 * the Run-Now button, the recent-jobs table, and the global
 * scheduler status banner. All three sections collapse cleanly when
 * the project has no ``data_source`` configured (you can't schedule
 * a reload of nothing).
 */

import {
  Badge,
  Button,
  Card,
  CardBody,
  Checkbox,
  Input,
  Modal,
  Select,
} from "@omnitok/ui";
import { PlayCircle, RefreshCw, XOctagon } from "lucide-react";
import { Spinner } from "@pervasivemind/bi-components";
import { useEffect, useState } from "react";

import { useT } from "@/i18n";
import {
  projectsApi,
  schedulerApi,
  type LoaderJob,
  type LoaderJobProgress,
  type LoaderReconciliation,
  type LoaderScheduleInput,
  type ProjectLoaderResponse,
  type SchedulerStatus,
} from "@/lib/builder-api";
import type { Project } from "@/types";

interface LoaderScheduleFieldsProps {
  project: Project;
}

const DEFAULT_SCHEDULE: LoaderScheduleInput = {
  enabled: false,
  cron: "0 3 * * *",
  timezone: "UTC",
  mode: "reload",
};

/** What the tab's first load got back — each call on its own (#258, #263). */
export interface LoaderTabLoad {
  overview: ProjectLoaderResponse | null;
  overviewError: string | null;
  status: SchedulerStatus | null;
  statusError: string | null;
}

export async function loadLoaderTab(slug: string): Promise<LoaderTabLoad> {
  const [ov, st] = await Promise.allSettled([
    projectsApi.loader.get(slug),
    schedulerApi.status(),
  ]);
  const message = (reason: unknown): string =>
    reason instanceof Error ? reason.message : String(reason);
  return {
    overview: ov.status === "fulfilled" ? ov.value : null,
    overviewError: ov.status === "rejected" ? message(ov.reason) : null,
    status: st.status === "fulfilled" ? st.value : null,
    statusError: st.status === "rejected" ? message(st.reason) : null,
  };
}

export function LoaderScheduleFields({
  project,
}: LoaderScheduleFieldsProps): JSX.Element {
  const { t } = useT();
  const [data, setData] = useState<ProjectLoaderResponse | null>(null);
  const [schedule, setSchedule] = useState<LoaderScheduleInput>(DEFAULT_SCHEDULE);
  const [status, setStatus] = useState<SchedulerStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);

  // Initial fetch — overview + global scheduler status in parallel.
  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      setLoading(true);
      const out = await loadLoaderTab(project.slug);
      if (cancelled) return;
      // A failed GET clears ``data`` too, so a slug change can never
      // leave the previous project's schedule on screen to be saved (#258).
      setData(out.overview);
      if (out.overview !== null) {
        const merged = { ...DEFAULT_SCHEDULE, ...out.overview.schedule };
        setSchedule(merged);
      }
      setError(out.overviewError);
      setStatus(out.status);
      setStatusError(out.statusError);
      setLoading(false);
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [project.slug]);

  const hasDataSource = Object.keys(project.data_source ?? {}).length > 0;

  // ── Handlers ────────────────────────────────────────────

  const patch = (next: Partial<LoaderScheduleInput>): void => {
    setSchedule((prev) => ({ ...prev, ...next }));
  };

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const updated = await projectsApi.loader.update(project.slug, schedule);
      const merged = { ...DEFAULT_SCHEDULE, ...updated.schedule };
      setData(updated);
      setSchedule(merged);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async (opts?: {
    forceReap?: boolean;
  }): Promise<void> => {
    setRunning(true);
    setError(null);
    try {
      await projectsApi.loader.runNow(project.slug, opts);
      // Refresh overview to pick up the new job + state.
      const overview = await projectsApi.loader.get(project.slug);
      setData(overview);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRunning(false);
    }
  };

  // Iter 29.2 — live progress poll. When a job is ``running`` in the
  // recent_jobs list, refresh every 5 s so the UI surfaces the
  // current phase without the admin having to click Refresh.
  const hasRunningJob = (data?.recent_jobs ?? []).some(
    (j) => j.status === "running" || j.status === "pending",
  );
  useEffect(() => {
    if (!hasRunningJob) return;
    let cancelled = false;
    const tick = setInterval(() => {
      if (cancelled) return;
      void projectsApi.loader.get(project.slug).then((fresh) => {
        if (!cancelled) setData(fresh);
      });
    }, 5000);
    return () => {
      cancelled = true;
      clearInterval(tick);
    };
  }, [hasRunningJob, project.slug]);

  // ── Render ──────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-neutral-500">
        <Spinner size={16} ariaLabel={t("loader.loading")} />
        {t("loader.loading")}
      </div>
    );
  }

  if (data === null) {
    // #258 — the GET failed: never draw the form over DEFAULT_SCHEDULE,
    // because its Save would PUT those invented values over the real cron.
    return (
      <div
        role="alert"
        className="rounded-lg border border-error/40 bg-error/5 px-4 py-2 text-sm text-error-dark"
      >
        <p>{t("loader.loadFailed")}</p>
        <p className="mt-1 whitespace-pre-wrap">{error}</p>
      </div>
    );
  }

  if (!hasDataSource) {
    return (
      <Card>
        <CardBody>
          <p className="text-sm text-neutral-600">
            {t("loader.noDataSource")}
          </p>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-lg border border-error/40 bg-error/5 px-4 py-2 text-sm text-error-dark">
          {error}
        </div>
      )}
      {statusError && (
        <div
          role="alert"
          className="rounded-lg border border-error/40 bg-error/5 px-4 py-2 text-sm text-error-dark"
        >
          {t("loader.statusLoadFailed", { error: statusError })}
        </div>
      )}

      {/* Global scheduler banner — shows whether the loop is alive +
          who's the leader. Read-only in this tab; the admin Scheduler
          page (separate) has the enable/disable toggle. */}
      {status && (
        <SchedulerStatusBanner status={status} />
      )}

      {/* Schedule form */}
      <Card variant="bordered">
        <CardBody className="space-y-3">
          <h3 className="text-sm font-semibold text-neutral-900">
            {t("loader.scheduleTitle")}
          </h3>
          <Checkbox
            label={t("loader.enabled")}
            checked={schedule.enabled}
            onCheckedChange={(next) => patch({ enabled: next })}
            disabled={saving}
          />

          <Input
            label={t("loader.cron")}
            value={schedule.cron}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              patch({ cron: e.target.value })
            }
            placeholder="0 3 * * *"
            disabled={saving}
            helperText={t("loader.cronHelp")}
            fullWidth
          />

          <Input
            label={t("loader.timezone")}
            value={schedule.timezone}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              patch({ timezone: e.target.value })
            }
            placeholder="UTC"
            disabled={saving}
            helperText={t("loader.timezoneHelp")}
            fullWidth
          />

          <Select
            label={t("loader.mode")}
            value={schedule.mode}
            onChange={(e) =>
              patch({ mode: e.target.value as LoaderScheduleInput["mode"] })
            }
            disabled={saving}
            fullWidth
            // Only a full reload exists: the scheduler always enqueues
            // it and the PUT refuses the other two with a 422. They stay
            // visible with the "Pronto" badge, same as the unfinished
            // send channels (DEC-15).
            options={[
              { value: "reload", label: t("loader.mode.reload") },
              {
                value: "incremental",
                label: `${t("loader.mode.incremental")} · ${t("loader.soon")}`,
                disabled: true,
              },
              {
                value: "rebuild",
                label: `${t("loader.mode.rebuild")} · ${t("loader.soon")}`,
                disabled: true,
              },
            ]}
          />

          {/* No per-run time limit exists yet. The field is shown so
              the capability is visible, but disabled, empty and never
              sent — a value here would promise a cut nobody makes. */}
          <Input
            label={`${t("loader.maxRuntime")} · ${t("loader.soon")}`}
            type="number"
            value=""
            disabled
            readOnly
            helperText={t("loader.maxRuntimeHelp")}
            fullWidth
          />

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="primary"
              onClick={() => void handleSave()}
              loading={saving}
              disabled={saving}
            >
              {t("common.save")}
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Run now */}
      <Card variant="bordered">
        <CardBody className="space-y-2">
          <h3 className="text-sm font-semibold text-neutral-900">
            {t("loader.runNowTitle")}
          </h3>
          <p className="text-sm text-neutral-600">
            {t("loader.runNowHelp")}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="accept"
              leftIcon={<PlayCircle size={16} />}
              onClick={() => void handleRunNow()}
              loading={running}
              disabled={running}
            >
              {t("loader.runNow")}
            </Button>
            {hasRunningJob && (
              <Button
                variant="danger"
                leftIcon={<XOctagon size={16} />}
                onClick={() => {
                  if (
                    window.confirm(
                      t("loader.forceReapConfirm"),
                    )
                  ) {
                    void handleRunNow({ forceReap: true });
                  }
                }}
                disabled={running}
              >
                {t("loader.forceReap")}
              </Button>
            )}
          </div>
          <p className="text-xs text-neutral-500">
            {hasRunningJob
              ? t("loader.forceReapHelp")
              : t("loader.runNowHint")}
          </p>
        </CardBody>
      </Card>

      {/* Recent jobs */}
      <Card variant="bordered">
        <CardBody className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-neutral-900">
              {t("loader.recentJobsTitle")}
            </h3>
            <RefreshButton
              onClick={async () => {
                const fresh = await projectsApi.loader.get(project.slug);
                setData(fresh);
              }}
            />
          </div>
          {data && data.recent_jobs.length > 0 ? (
            <RecentJobsTable jobs={data.recent_jobs} slug={project.slug} />
          ) : (
            <p className="text-sm text-neutral-500">{t("loader.noJobs")}</p>
          )}
        </CardBody>
      </Card>
    </div>
  );
}


// ── Helpers ──────────────────────────────────────────────────────────


function SchedulerStatusBanner({
  status,
}: {
  status: SchedulerStatus;
}): JSX.Element {
  const { t } = useT();
  const variant: "success" | "neutral" | "warning" =
    status.enabled && status.running
      ? "success"
      : status.enabled
        ? "warning"
        : "neutral";
  const label = status.enabled
    ? status.running
      ? t("loader.statusActive")
      : t("loader.statusIdle")
    : t("loader.statusDisabled");
  return (
    <div className="flex items-center gap-3 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs">
      <Badge variant={variant}>{label}</Badge>
      {status.lease_holder && (
        <span className="text-neutral-500">
          {t("loader.leaseHolder")}: <code>{status.lease_holder}</code>
        </span>
      )}
      {status.last_tick_at && (
        <span className="text-neutral-500">
          {t("loader.lastTick")}:{" "}
          {parseAsUTC(status.last_tick_at)?.toLocaleString() ?? "—"}
        </span>
      )}
    </div>
  );
}


function RefreshButton({
  onClick,
}: {
  onClick: () => Promise<void>;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        setBusy(true);
        try {
          await onClick();
        } finally {
          setBusy(false);
        }
      }}
      disabled={busy}
      className="text-neutral-500 hover:text-primary-dark disabled:opacity-50"
      aria-label="Refresh"
    >
      <RefreshCw size={14} className={busy ? "animate-spin" : ""} />
    </button>
  );
}


function RecentJobsTable({
  jobs,
  slug,
}: {
  jobs: LoaderJob[];
  slug: string;
}): JSX.Element {
  // Iter 32 — clicking a job's Reconciliation badge opens the
  // per-element detail drawer. ``null`` = closed.
  const [detailJob, setDetailJob] = useState<LoaderJob | null>(null);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-neutral-500">
          <tr className="border-b border-neutral-200">
            <th className="px-2 py-1 text-left">Status</th>
            <th className="px-2 py-1 text-left">Mode</th>
            <th className="px-2 py-1 text-left">Started</th>
            <th className="px-2 py-1 text-left">Duration</th>
            <th className="px-2 py-1 text-left">Result / Phase</th>
            <th className="px-2 py-1 text-left">Reconciliation</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((j) => (
            <tr key={j.id} className="border-b border-neutral-100">
              <td className="px-2 py-1">
                <StatusBadge status={j.status} />
              </td>
              <td className="px-2 py-1 text-neutral-600">{j.mode}</td>
              <td className="px-2 py-1 text-neutral-600">
                {parseAsUTC(j.started_at)?.toLocaleString() ?? "—"}
              </td>
              <td className="px-2 py-1 text-neutral-600">
                {formatDuration(j.started_at, j.finished_at)}
              </td>
              <td className="px-2 py-1 text-neutral-600">
                {j.status === "running" || j.status === "pending" ? (
                  <ProgressCell progress={j.progress_json} />
                ) : j.error_summary ? (
                  <span className="text-error-dark">{j.error_summary.slice(0, 60)}</span>
                ) : j.result_json ? (
                  // For success, surface ``rows`` if present.
                  <span>
                    {String((j.result_json as Record<string, unknown>).rows ?? "—")}{" "}
                    rows
                  </span>
                ) : (
                  "—"
                )}
              </td>
              <td className="px-2 py-1">
                <ReconCell
                  resultJson={j.result_json}
                  onClick={() => setDetailJob(j)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {detailJob && (
        <ReconDetailModal
          slug={slug}
          job={detailJob}
          onClose={() => setDetailJob(null)}
        />
      )}
    </div>
  );
}


/**
 * Iter 32 — reconciliation badge for a finished job.
 *
 * Reads ``result_json.reconciliation``. Two shapes are handled:
 *   - success: ``{gate: {passed, failed, coverage_gaps}, portal: {...},
 *     portal_warning}``
 *   - gate-blocked failure: the flat gate summary
 *     ``{passed, failed, coverage_gaps}`` (+ ``gate_blocked`` on the job).
 * Green when the gate passed clean, red on any gate mismatch/gap, plus an
 * amber "portal" chip when the post-publish portal phase disagreed.
 *
 * When ``onClick`` is supplied and the job has reconciliation data, the
 * badge becomes a button that opens the per-element detail drawer.
 */
export function ReconCell({
  resultJson,
  onClick,
}: {
  resultJson: Record<string, unknown> | null | undefined;
  onClick?: () => void;
}): JSX.Element {
  const { t } = useT();
  const recon = (resultJson?.reconciliation ?? null) as Record<
    string,
    unknown
  > | null;
  if (!recon) return <span className="text-neutral-500">—</span>;

  const asCount = (o: unknown, k: string): number => {
    const v = (o as Record<string, unknown> | null)?.[k];
    return typeof v === "number" ? v : 0;
  };

  // Resolve the gate summary from either shape.
  const gate = ("gate" in recon ? recon.gate : recon) as Record<
    string,
    unknown
  >;
  const gPassed = asCount(gate, "passed");
  const gFailed = asCount(gate, "failed");
  const gGaps = asCount(gate, "coverage_gaps");
  const gateBad = gFailed + gGaps > 0;
  // #264 — nothing compared is not a pass: no "✓ 0".
  const gateNoChecks = !gateBad && gPassed === 0;

  const portal = ("portal" in recon ? recon.portal : null) as Record<
    string,
    unknown
  > | null;
  const portalWarning = recon.portal_warning === true;
  const pPassed = asCount(portal, "passed");

  const inner = (
    <>
      {gateNoChecks ? (
        <Badge variant="neutral" title={t("loader.recon.noChecksTitle")}>
          {t("loader.recon.noChecks")}
        </Badge>
      ) : (
        <Badge variant={gateBad ? "error" : "success"}>
          {gateBad ? `✗ ${gFailed + gGaps}` : `✓ ${gPassed}`}
        </Badge>
      )}
      {portal !== null &&
        (portalWarning ? (
          <Badge variant="warning">⚠ portal</Badge>
        ) : pPassed > 0 ? (
          <span className="text-neutral-500" title="portal matches">
            ✓
          </span>
        ) : (
          <span
            className="text-neutral-500"
            title={t("loader.recon.portalNotChecked")}
          >
            – portal
          </span>
        ))}
    </>
  );

  if (!onClick) {
    return <span className="inline-flex items-center gap-1">{inner}</span>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded hover:bg-neutral-100 px-1 -mx-1 cursor-pointer"
      title="View reconciliation detail"
    >
      {inner}
    </button>
  );
}


/** Human label for a reconciliation ``layer`` value. */
function layerLabel(layer: string, t: (k: string) => string): string {
  if (layer === "A_vs_B") return t("loader.recon.layerGate");
  if (layer === "B_vs_C_cache") return t("loader.recon.layerPortalCache");
  if (layer === "B_vs_C_http") return t("loader.recon.layerPortalHttp");
  return layer;
}

/** Tailwind classes for a status chip. */
function statusChipClass(status: string): string {
  if (status === "pass") return "bg-success/15 text-success-dark";
  if (status === "fail" || status === "error") return "bg-error/10 text-error-dark";
  if (status === "exempt") return "bg-neutral-100 text-neutral-500";
  return "bg-neutral-100 text-neutral-600";
}

/**
 * Iter 32 — per-element reconciliation detail drawer.
 *
 * Fetches every ``loader_reconciliations`` row for a finished job and
 * groups them by report. Each row shows the layer (gate A·B / portal
 * B·C), status, golden vs observed value, and delta — the full
 * breakdown the badge in the Recent jobs table only summarises.
 */
function ReconDetailModal({
  slug,
  job,
  onClose,
}: {
  slug: string;
  job: LoaderJob;
  onClose: () => void;
}): JSX.Element {
  const { t } = useT();
  const [rows, setRows] = useState<LoaderReconciliation[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    projectsApi.loader
      .listReconciliations(slug, job.id)
      .then((res) => {
        if (alive) setRows(res.reconciliations);
      })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      alive = false;
    };
  }, [slug, job.id]);

  // Group rows by report_key, preserving the backend's sort order.
  const groups: { report: string; rows: LoaderReconciliation[] }[] = [];
  for (const r of rows ?? []) {
    let g = groups.find((x) => x.report === r.report_key);
    if (!g) {
      g = { report: r.report_key, rows: [] };
      groups.push(g);
    }
    g.rows.push(r);
  }

  return (
    <Modal open onClose={onClose} title={t("loader.recon.title")}>
      <div className="text-xs text-neutral-500 mb-3">
        {t("loader.recon.jobLabel")}{" "}
        <span className="font-mono">{job.id.slice(0, 8)}</span> ·{" "}
        {parseAsUTC(job.started_at)?.toLocaleString() ?? "—"}
      </div>

      {error && <p className="text-sm text-error-dark">{error}</p>}
      {!error && rows === null && (
        <div className="flex items-center gap-2 text-sm text-neutral-500 py-6">
          <Spinner size={16} ariaLabel={t("loader.recon.loading")} />
          {t("loader.recon.loading")}
        </div>
      )}
      {!error && rows !== null && rows.length === 0 && (
        <p className="text-sm text-neutral-500 py-6">
          {t("loader.recon.empty")}
        </p>
      )}

      {groups.map((g) => (
        <div key={g.report} className="mb-5">
          <h4 className="text-sm font-semibold text-neutral-700 mb-1">
            {g.report}
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-neutral-500">
                <tr className="border-b border-neutral-200">
                  <th className="px-2 py-1 text-left">
                    {t("loader.recon.colElement")}
                  </th>
                  <th className="px-2 py-1 text-left">
                    {t("loader.recon.colLayer")}
                  </th>
                  <th className="px-2 py-1 text-left">
                    {t("loader.recon.colStatus")}
                  </th>
                  <th className="px-2 py-1 text-right">
                    {t("loader.recon.colGolden")}
                  </th>
                  <th className="px-2 py-1 text-right">
                    {t("loader.recon.colObserved")}
                  </th>
                  <th className="px-2 py-1 text-right">
                    {t("loader.recon.colDelta")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {g.rows.map((r) => (
                  <tr key={r.id} className="border-b border-neutral-100">
                    <td className="px-2 py-1">
                      <span className="text-neutral-500 mr-1">
                        [{r.element_type}]
                      </span>
                      {r.element_id}
                    </td>
                    <td className="px-2 py-1 text-neutral-600">
                      {layerLabel(r.layer, t)}
                    </td>
                    <td className="px-2 py-1">
                      <span
                        className={`inline-block rounded px-1.5 py-0.5 ${statusChipClass(
                          r.status,
                        )}`}
                        title={r.message ?? undefined}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="px-2 py-1 text-right font-mono text-neutral-700">
                      {r.golden_value ?? "—"}
                    </td>
                    <td className="px-2 py-1 text-right font-mono text-neutral-700">
                      {r.actual_value ?? "—"}
                    </td>
                    <td className="px-2 py-1 text-right font-mono text-neutral-500">
                      {r.delta === null || r.delta === undefined
                        ? "—"
                        : r.delta}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </Modal>
  );
}


/**
 * Render the live phase tracker for a running job. Iter 29.2.
 *
 * The backend writes a richer dict (``view``, ``index``/``total``,
 * ``elapsed_ms``, ``row_count``, etc.) but for the recent-jobs row
 * we summarise to one line: ``phase`` plus the most useful contextual
 * field. ``materialize_view`` gets ``view (idx/total)``,
 * ``materialize_raw_complete`` gets the elapsed ms, etc.
 */
function ProgressCell({
  progress,
}: {
  progress: LoaderJobProgress | null;
}): JSX.Element {
  // Hook BEFORE the early return — the hook counter has to stay
  // stable across renders (React #310 guard).
  const { t } = useT();
  if (!progress) {
    return <span className="text-neutral-500 italic">starting…</span>;
  }
  const { phase, view, index, total, elapsed_ms, row_count, error } = progress;
  const detail = (() => {
    if (phase === "materialize_view_start" || phase === "materialize_view_complete") {
      return view && index && total ? `${view} (${index}/${total})` : view ?? "";
    }
    if (phase === "materialize_view_failed") {
      return `${view ?? ""} — ${error?.slice(0, 40) ?? "error"}`;
    }
    if (phase === "audit_snapshot_complete") {
      return row_count != null ? `${row_count.toLocaleString()} rows` : "";
    }
    if (phase === "materialize_raw_complete") {
      return elapsed_ms != null ? `(${(elapsed_ms / 1000).toFixed(1)}s)` : "";
    }
    if (phase === "view_defs_resolved") {
      return progress.view_count != null
        ? `${progress.view_count} views to materialize`
        : "";
    }
    return "";
  })();
  return (
    <span className="flex items-center gap-1">
      <Spinner size={12} ariaLabel={t("loader.loading")} />
      <code className="text-xs font-mono text-neutral-700">{phase}</code>
      {detail && <span className="text-neutral-500">— {detail}</span>}
    </span>
  );
}


function StatusBadge({
  status,
}: {
  status: LoaderJob["status"];
}): JSX.Element {
  // BadgeVariant: 'primary' | 'accent' | 'neutral' | 'success' |
  // 'warning' | 'error' | 'info' | 'violet' | 'rose' | 'teal'.
  // Note: Badge uses ``error`` while Button uses ``danger`` for the
  // "things went bad" colour — they don't share a vocabulary.
  const variant: "success" | "error" | "info" | "neutral" =
    status === "completed"
      ? "success"
      : status === "failed"
        ? "error"
        : status === "running"
          ? "info"
          : "neutral";
  return <Badge variant={variant}>{status}</Badge>;
}


/**
 * Backend serialises ``LoaderJob.started_at`` / ``finished_at`` /
 * ``created_at`` from naive UTC DateTime columns. Pydantic default
 * emits ISO 8601 WITHOUT a timezone marker, so ``new Date("...")``
 * in JS interprets the string as the BROWSER's local zone — which
 * for an Argentina (UTC-3) user means the timestamp ends up 3 hours
 * in the wrong direction (e.g. duration goes negative).
 *
 * This helper appends ``Z`` if the string has no timezone suffix,
 * forcing UTC interpretation. Until the DB columns are migrated to
 * ``DateTime(timezone=True)`` (iter 29.x), this is the canonical
 * place to coerce.
 */
function parseAsUTC(iso: string | null): Date | null {
  if (!iso) return null;
  const hasTz = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(iso);
  return new Date(hasTz ? iso : iso + "Z");
}


function formatDuration(
  startedAt: string | null,
  finishedAt: string | null,
): string {
  const startDate = parseAsUTC(startedAt);
  if (!startDate) return "—";
  const endDate = parseAsUTC(finishedAt) ?? new Date();
  const ms = endDate.getTime() - startDate.getTime();
  if (ms < 0) {
    // Should not happen post-parseAsUTC, but guard anyway —
    // clock drift between client + server can produce small negatives.
    return "—";
  }
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${(ms / 60_000).toFixed(1)} min`;
}
